<template>
    <DsSheet
        v-model:open="isOpen"
        :primaryLoading="isFetching"
        primaryLabel="Save"
        secondaryLabel="Cancel"
        size="md"
        title="Edit file"
        @clickPrimary="executeEditFile"
        @clickSecondary="isOpen = false"
    >
        <div
            v-if="file"
            class="grid gap-4"
        >
            <DsTextInput
                v-model.trim="refNumber"
                :error="errors.ref_number"
                label="File reference #"
                required
                type="text"
            />
            <DsAddressAutocompleteSearch
                v-model="address"
                id="edit_file_address"
                :key="isOpen ? 'open' : 'closed'"
                :error="errors.address"
                label="Address"
                required
                @locationCleared="address = ''"
                @locationSelected="handleLocationSelected"
            />
            <div class="grid gap-4 sm:grid-cols-2">
                <DsDatePicker
                    v-model="closingDateInputValue"
                    v-model:openPicker="isClosingDatePickerOpen"
                    :input="{ label: 'Closing date' }"
                    :minValue="createToday().subtract({ years: 5 })"
                    required
                />
                <DsMoneyInput
                    v-model="balanceDue"
                    :error="errors.balance_due"
                    label="Balance due"
                    :moneyConfig="{ prefix: '$', disableNegative: true }"
                    type="text"
                />
            </div>
        </div>
    </DsSheet>
</template>

<script setup lang="ts">
import type { GooglePlaceDetailsResponse } from '@/components/design-system/form/input/DsAddressAutocomplete/address-autocomplete.types';
import DsAddressAutocompleteSearch from '@/components/design-system/form/input/DsAddressAutocomplete/DsAddressAutocompleteSearch.vue';
import useDsToast from '@/components/design-system/toast/useDsToast';
import type { FileDetails, UpdateFilePayload } from '@/components/Views/Company/File/file.types';
import { createCalendarDate, createToday } from '@/shared/utils/calendar-date-utils';
import { formatDate, MONTH_DAY_YEAR_FORMAT } from '@/shared/utils/format-date';
import { cents, type Cents } from '@/shared/utils/money';
import { useFileDetailStore } from '@/stores/company/file-detail.store';
import DsSheet from '@ds/DsSheet.vue';
import DsDatePicker from '@ds/form/date-picker/DsDatePicker.vue';
import DsMoneyInput from '@ds/form/input/DsMoneyInput.vue';
import DsTextInput from '@ds/form/input/text/DsTextInput.vue';
import dayjs from 'dayjs';
import type { DateValue } from 'reka-ui';
import { computed, ref, watch } from 'vue';
import * as yup from 'yup';

const props = defineProps<{
    file: Pick<FileDetails, 'id' | 'ref_number' | 'address' | 'balance_due' | 'close_date'>;
}>();

const isOpen = defineModel<boolean>('open');

const validationSchema = yup.object({
    ref_number: yup.string().required('Add file reference #'),
    address: yup.string().required('Add address'),
    balance_due: yup.number().nullable(),
    close_date: yup.string().nullable(),
});

const refNumber = ref('');
const address = ref('');
const balanceDue = ref<Cents>(cents(0));
const closeDate = ref('');
const errors = ref<Record<string, string>>({});

const fileDetailStore = useFileDetailStore();

const { isFetching, updateFile } = fileDetailStore.useEditFile(props.file.id);

const { addToast } = useDsToast();

const toastSuccessMessage = 'File changes saved';
const toastErrorMessage = 'File changes could not be saved. Please try again.';

const isClosingDatePickerOpen = ref(false);
const closingDateInputValue = computed({
    get: () => {
        if (!closeDate.value) return undefined;
        const date = dayjs(closeDate.value, 'MM/DD/YYYY');
        return date.isValid() ? createCalendarDate(date.year(), date.month() + 1, date.date()) : undefined;
    },
    set: (value: DateValue | undefined) => {
        if (value) {
            closeDate.value = dayjs(value.toString()).format(MONTH_DAY_YEAR_FORMAT);
        } else {
            closeDate.value = '';
        }
    },
});

const handleLocationSelected = (event: GooglePlaceDetailsResponse | null) => {
    if (event?.formattedAddress) {
        address.value = event.formattedAddress;
    } else {
        address.value = '';
    }
};

watch(
    [address, refNumber],
    ([newAddress, newRefNumber]) => {
        if (newAddress) {
            errors.value.address = '';
        }

        if (newRefNumber) {
            errors.value.ref_number = '';
        }
    },
    { immediate: true },
);

const emit = defineEmits<{
    (e: 'saved'): void;
}>();

const executeEditFile = async () => {
    try {
        await validationSchema.validate(
            {
                ref_number: refNumber.value,
                address: address.value,
                balance_due: balanceDue.value,
                close_date: closeDate.value,
            },
            { abortEarly: false },
        );
        errors.value = {};
    } catch (validationError) {
        if (validationError instanceof yup.ValidationError) {
            const newErrors: Record<string, string> = {};
            validationError.inner.forEach(error => {
                if (error.path) {
                    newErrors[error.path] = error.message;
                }
            });
            errors.value = newErrors;
        }
        return;
    }

    const formData: UpdateFilePayload = {
        ref_number: refNumber.value,
        address: address.value,
        balance_due: balanceDue.value,
        close_date: closeDate.value,
    };

    try {
        await updateFile(formData);
        emit('saved');
        isOpen.value = false;
        addToast({
            detail: toastSuccessMessage,
        });
    } catch (error) {
        if (error && typeof error === 'object' && 'errors' in error) {
            const backendErrors: Record<string, string> = {};
            Object.entries(error.errors as Record<string, string[]>).forEach(([key, value]) => {
                backendErrors[key] = Array.isArray(value) ? value[0] : String(value);
            });

            errors.value = { ...errors.value, ...backendErrors };
            return;
        }

        addToast({
            detail: toastErrorMessage,
            severity: 'error',
        });
    }
};

watch(isOpen, newVal => {
    if (newVal) {
        refNumber.value = props.file.ref_number || '';
        address.value = props.file.address || '';
        balanceDue.value = props.file.balance_due ?? cents(0);
        closeDate.value = formatDate(props.file.close_date) || '';
    }
});
</script>
